import { Input, Modal, Tree, Empty } from '@maxtropy/components';
import { FC, useState, useEffect, useMemo } from 'react';
import { getAllOrganizationTree, OrganizationTreeProps } from '../../../../../api/customer';
import { DataNode } from 'antd/es/tree';
import StatusButton from './StatusButton';
import { OrgDataPermissionType, OrgDataPermissionTypeDisplay } from '../../util';
type NewDataNode = DataNode & {
  originalChildren?: OrganizationTreeProps[];
};

interface OrgModalProps {
  type: OrgDataPermissionType;
  selectedData: string[];
  setSelectedData: (value: string[]) => void;
  onCancel: () => void;
}

function getChildren(tree: OrganizationTreeProps[]) {
  let childrenKeys: string[] = [];
  const loop = (data: OrganizationTreeProps[], arr: string[] = []) => {
    for (let item of data) {
      arr.push(item.data!.mcid);
      if (item.children && item.children.length) loop(item.children, arr);
    }
    return arr;
  };
  const rev = (data: OrganizationTreeProps[]) => {
    for (let i = 0; i < data.length; i++) {
      const node = data[i];
      childrenKeys.push(node.data?.mcid!);
      childrenKeys.push(...loop(node.children));
    }
  };
  rev(tree);
  return childrenKeys;
}

function formatTreeData(data: OrganizationTreeProps[], parentList?: string[]) {
  return data.map(item => {
    const res: DataNode = {
      ...item,
      key: item.data!.mcid,
      disabled: parentList?.includes(item.data!.mcid) || !item.data?.hasPermission,
      title: item.data?.name,
      children: formatTreeData(item.children, parentList),
    };
    return res;
  });
}

function flattenTreeData(data: OrganizationTreeProps[]): NewDataNode[] {
  const result: NewDataNode[] = [];

  const traverse = (nodes: OrganizationTreeProps[]) => {
    nodes.forEach(node => {
      const formattedNode: NewDataNode = {
        ...node,
        key: node.data!.mcid,
        title: node.data?.name,
        disabled: !node.data?.hasPermission,
        children: [],
        originalChildren: node.children && node.children.length > 0 ? node.children : undefined,
      };
      result.push(formattedNode);
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    });
  };

  traverse(data);
  return result;
}

const generateList = (list: DataNode[]) => {
  const dataList: string[] = [];
  const rev = (list: DataNode[]) => {
    for (let i = 0; i < list.length; i++) {
      const node = list[i];
      const { key } = node;
      dataList.push(key as string);
      if (node.children) {
        rev(node.children);
      }
    }
  };
  rev(list);
  return dataList;
};

const traverseDataNodes = (dataNodes: DataNode[], callback: (data: DataNode, childKeys: string[]) => void) => {
  function processNode(node: DataNode) {
    const children = node ? node.children : dataNodes;

    if (node) {
      const _childKeys: string[] = [];
      const dig = (dataNodes: DataNode[]) => {
        (dataNodes ?? []).forEach(item => {
          if (!item.disabled) {
            _childKeys.push(item.key as string);
          }
          dig(item.children ?? []);
        });
      };
      dig(node.children ?? []);
      callback(node, _childKeys);
    }

    if (children) {
      children.forEach(subNode => {
        processNode(subNode);
      });
    }
  }

  processNode(null as unknown as DataNode);
};

const convertDataToEntities = (dataNodes: DataNode[]) => {
  const keyEntities: Record<string, string[]> = {};
  traverseDataNodes(dataNodes, (item, childKeys) => {
    const { key } = item;
    keyEntities[key] = childKeys;
  });
  return keyEntities;
};
const OrgModal: FC<OrgModalProps> = ({ selectedData, setSelectedData, onCancel, type }) => {
  const [checked, setChecked] = useState<boolean>(false);
  const [treeData, setTreeData] = useState<OrganizationTreeProps[]>([]);
  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [dataList, setDataList] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [flattenedData, setFlattenedData] = useState<DataNode[]>([]);

  useEffect(() => {
    if (selectedData.length > 0) {
      setCheckedList(selectedData);
    }
  }, []);

  useEffect(() => {
    getAllOrganizationTree().then(data => {
      if (data) {
        setTreeData([data]);
        setDataList(generateList(formatTreeData([data])));
        setFlattenedData(flattenTreeData([data]));
      }
    });
  }, []);

  const onOk = () => {
    console.log('selectedData', checkedList);
    setSelectedData(checkedList);
    onCancel();
  };

  const list = useMemo(() => {
    if (isSearching) {
      return flattenedData.filter(item => (item.title as string)?.toLowerCase().includes(searchValue.toLowerCase()));
    }
    const childrenList = getChildren(treeData);
    const parentsList = dataList.filter(a => {
      return !childrenList.some(c => c === a);
    });
    const intersection = parentsList.filter(v => checkedList.indexOf(v) > -1);
    if (intersection && intersection.length > 0) {
      setChecked(true);
    }
    if (treeData && !checked) {
      return formatTreeData(treeData, parentsList);
    } else {
      return formatTreeData(treeData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, checked, searchValue, dataList]);

  // 在组件内部添加一个函数来获取原始树结构的实体映射
  const getOriginalKeyEntities = useMemo(() => {
    if (!treeData || treeData.length === 0) return {};

    const keyEntities: Record<string, string[]> = {};
    const processNode = (node: OrganizationTreeProps) => {
      const key = node.data!.mcid;
      const childKeys: string[] = [];

      const collectChildKeys = (children: OrganizationTreeProps[]) => {
        children.forEach(child => {
          const childKey = child.data!.mcid;
          childKeys.push(childKey);
          if (child.children && child.children.length > 0) {
            collectChildKeys(child.children);
          }
        });
      };

      if (node.children && node.children.length > 0) {
        collectChildKeys(node.children);
      }

      keyEntities[key] = childKeys;

      if (node.children && node.children.length > 0) {
        node.children.forEach(child => processNode(child));
      }
    };

    treeData.forEach(node => processNode(node));
    return keyEntities;
  }, [treeData]);
  const dataKeyEntities: Record<string, string[]> = useMemo(() => {
    if (isSearching) {
      // 在搜索模式下使用原始树结构的实体映射
      return getOriginalKeyEntities;
    }
    return Array.isArray(list) && list.length > 0 ? convertDataToEntities(list) : {};
  }, [list]);
  const selectAllChild = (key: string, value: boolean) => {
    // 在平铺模式下，需要找到原始节点来获取所有子节点
    if (isSearching) {
      // 从原始树数据中找到对应的节点
      const findNode = (nodes: OrganizationTreeProps[]): OrganizationTreeProps | null => {
        for (const node of nodes) {
          if (node.data!.mcid === key) {
            return node;
          }
          if (node.children && node.children.length > 0) {
            const found = findNode(node.children);
            if (found) return found;
          }
        }
        return null;
      };

      const node = findNode(treeData);
      if (node && node.children) {
        // 获取所有子节点的key
        const getChildKeys = (children: OrganizationTreeProps[]): string[] => {
          let keys: string[] = [];
          children.forEach(child => {
            keys.push(child.data!.mcid);
            if (child.children && child.children.length > 0) {
              keys = keys.concat(getChildKeys(child.children));
            }
          });
          return keys;
        };

        const allChildKeys = getChildKeys(node.children);

        if (value) {
          // 选中所有子节点
          setCheckedList(Array.from(new Set([...checkedList, ...allChildKeys])));
        } else {
          // 取消选中所有子节点
          setCheckedList(Array.from(new Set(checkedList.filter(item => !allChildKeys.includes(item)))));
        }
      }
    } else {
      // 正常树形模式下的逻辑
      if (value) {
        setCheckedList(Array.from(new Set([...checkedList, ...dataKeyEntities[key]])));
      } else {
        setCheckedList(Array.from(new Set(checkedList.filter(item => !dataKeyEntities[key].includes(item)))));
      }
    }
  };

  const titleRender = (node: NewDataNode) => {
    const hasChildren = isSearching
      ? node.originalChildren && node.originalChildren.length > 0
      : Array.isArray(node.children) && node.children.length > 0;

    const visible = !node.disabled && hasChildren;
    // 修改checked的计算方式，使其在平铺模式下也能正确工作
    let checked = false;
    if (dataKeyEntities[node.key]) {
      // 如果在平铺模式下，需要通过原始树结构来获取子节点
      if (isSearching && node.originalChildren) {
        // 获取原始子节点的key列表
        const getChildKeys = (children: OrganizationTreeProps[]): string[] => {
          let keys: string[] = [];
          children.forEach(child => {
            keys.push(child.data!.mcid);
            if (child.children && child.children.length > 0) {
              keys = keys.concat(getChildKeys(child.children));
            }
          });
          return keys;
        };

        const allChildKeys = getChildKeys(node.originalChildren);
        // 检查所有子节点是否都被选中
        checked = allChildKeys.length > 0 && allChildKeys.every(key => checkedList.includes(key));
      } else {
        // 正常树形模式下的检查逻辑
        checked = dataKeyEntities[node.key].every(value => checkedList.includes(value));
      }
    }
    return (
      <div>
        <span>{node?.title as React.ReactNode}</span>
        {visible && (
          <StatusButton
            style={{ margin: '2px 5px' }}
            checked={checked}
            onChange={(event, value) => selectAllChild(node.key as string, value)}
          >
            全选子组织
          </StatusButton>
        )}
      </div>
    );
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setIsSearching(value.trim() !== '');
  };

  return (
    <Modal
      title={OrgDataPermissionTypeDisplay[type]}
      open={true}
      size="big"
      onOk={onOk}
      onCancel={onCancel}
      destroyOnClose
    >
      <Input.Search
        allowClear
        placeholder="请输入组织名称"
        style={{ width: 340, marginBottom: 10 }}
        onSearch={v => handleSearch(v)}
      />
      {list && list.length > 0 ? (
        <Tree
          checkStrictly
          checkable
          checkedKeys={checkedList}
          treeData={list}
          defaultExpandAll
          onCheck={(checkedKeys: any) => setCheckedList(checkedKeys.checked)}
          titleRender={titleRender}
        />
      ) : (
        <Empty style={{ marginTop: '10%' }} />
      )}
    </Modal>
  );
};

export default OrgModal;
